import { useEffect, useState } from "react";
import { ManageOrdersViewModel } from "../../viewmodels/orders/ManageOrdersViewModel";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import * as actions from "../../store/actions/index";
import { formatDateWithTime, withRouter, convertStatus } from "../../shared/utility";

// Template Components
import DataManagementTemplate from "../../components/templates/DataManagementTemplate";

// Other Components
import EmptyModal from "../../components/modals/EmptyModal";
import SlideOver from "../../components/slideOver/SlideOver";
import Pills, { PillType } from "../../components/controls/Pills";
import OrdersCardsForMobile from "./OrdersCardsForMobile";

function ManageOrders(props) {
  const { logout } = props;
  const navigate = useNavigate();
  const {
    filterComponents,
    getCustomersForDropdown,
    getUsersForDropdown,
    getOrders,
    loading,
    orders,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    askDeleteOrder,
    filters,
    resetFilters,
    setShowFilters,
    showFilters,
    openImportOrder,
  } = ManageOrdersViewModel(logout);

  var currentPage = 1;
  useEffect(() => {
    getCustomersForDropdown();
    getOrders(null, currentPage, filters);
    getUsersForDropdown();
  }, []);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");

  // Search handler
  const handleSearch = (term) => {
    setSearchTerm(term);
    getOrders(term || null, currentPage, filters);
  };

  // Pagination handler
  const handlePageChange = (event) => {
    currentPage = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    getOrders(searchTerm || null, currentPage, filters);
  };

  // Row click handler
  const handleRowClick = (order) => {
    navigate(`/orders/${order.id}`);
  };


  // Button configurations
  const headerButtons = [
    {
      label: "Create Order",
      onClick: () => navigate("/create-order"),
      className: "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white focus:ring-primary-500"
    },
    {
      label: "Import Orders", 
      onClick: () => openImportOrder(),
      className: "bg-white border border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300 focus:ring-primary-500"
    }
  ];

  // Column configurations for DataTable
  const columns = [
    {
      field: "po_number",
      header: "PO Number",
      style: { width: '15%' },
      body: (rowData) => (
        <span className="font-semibold text-primary-600 apollo-subheading">
          {rowData.po_number}
        </span>
      )
    },
    {
      field: "company_name",
      header: "Customer",
      style: { width: '25%' },
      body: (rowData) => (
        <span className="apollo-body">{rowData.company_name}</span>
      )
    },
    {
      field: "created_at",
      header: "Date",
      style: { width: '20%' },
      body: (rowData) => (
        <span className="apollo-body">{formatDateWithTime(rowData.created_at)}</span>
      )
    },
    {
      field: "total_dollar_amount",
      header: "Total",
      style: { width: '15%' },
      body: (rowData) => (
        <span className="font-semibold text-primary-700 apollo-subheading">
          ${rowData.total_dollar_amount}
        </span>
      )
    },
    {
      field: "status",
      header: "Status",
      style: { width: '15%' },
      body: (rowData) => <Pills content={convertStatus(rowData.status)} />
    },
    {
      header: "Actions",
      style: { width: '10%' },
      body: (rowData) => (
        <div className="flex gap-2">
          <Link to={`/orders/${rowData.id}`}>
            <button
              className="inline-flex items-center justify-center px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Edit
            </button>
          </Link>
          <button 
            onClick={() => askDeleteOrder(rowData)}
            className="inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  // Mobile cards component
  const OrdersCardsMobile = ({ data }) => (
    <OrdersCardsForMobile
      askDeleteOrder={askDeleteOrder}
      orders={{ data: data }}
    />
  );

  // Filter configuration
  const filterConfig = {
    dropdown: true,
    components: filterComponents,
    onReset: () => resetFilters(),
    onApply: () => {
      setShowFilters(false);
      getOrders(searchTerm || null, currentPage, filters);
    }
  };

  return (
    <div>
      <DataManagementTemplate
        title="Orders Management"
        description="Track and manage your orders efficiently"
        headerButtons={headerButtons}
        filterConfig={filterConfig}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        columns={columns}
        data={orders.data || []}
        loading={loading}
        pagination={{
          first: first,
          rows: rows,
          total: orders.total || 0,
          template: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
          currentPageReportTemplate: "Showing {first} to {last} of {totalRecords} orders"
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        searchPlaceholder="Search by customer name or PO number..."
        MobileCardsComponent={OrdersCardsMobile}
        emptyMessage="No orders found"
      />
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        customClass="max-w-3xl"
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ManageOrders)
);
